import { StatusCodes } from '@e3dc-react/shell/Libs'
import { AuthFetchError, AuthFetchWithResponse } from '@e3dc-react/shell/Types/api'
import { ApiResult } from 'src/Libs/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { Client, ClientWithSecret } from '../models'
import { ClientDto, ClientWithSecretDto } from './dto'
import clientToModelTransformer from '../transformers/client-to-model.transformer'
import clientWithSecretToModelTransformer from '../transformers/client-with-secret-to-model.transformer'

/**
 * Fetches the API access client belonging to a user.
 */
export const getClient = async (uid: number | undefined, authFetch: AuthFetchWithResponse): Promise<ApiResult<Client, AuthFetchError | Error>> => {
  if (!uid) {
    return new ApiResult<Client, AuthFetchError | Error>(null, true, new Error('No user id provided'))
  }

  try {
    const response = await authFetch<ClientDto>({
      path: `hager-energy-api/users/${uid}/client`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult(clientToModelTransformer(response.data))
    }

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<Client, AuthFetchError | Error>(null, false)
    }

    return new ApiResult<Client, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<Client>(error)
  }
}

/**
 * Creates an API access client for a user. The returned secret is only ever provided on creation.
 */
export const createClient = async (
  uid: number | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ClientWithSecret, AuthFetchError | Error>> => {
  if (!uid) {
    return new ApiResult<ClientWithSecret, AuthFetchError | Error>(null, true, new Error('No user id provided'))
  }

  try {
    const response = await authFetch<ClientWithSecretDto>({
      path: `hager-energy-api/users/${uid}/client`,
      method: 'POST',
    })

    if (response.status === StatusCodes.OK || response.status === StatusCodes.CREATED) {
      return new ApiResult(clientWithSecretToModelTransformer(response.data))
    }

    return new ApiResult<ClientWithSecret, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<ClientWithSecret>(error)
  }
}

/**
 * Deletes the API access client belonging to a user.
 */
export const deleteClient = async (uid: number | undefined, authFetch: AuthFetchWithResponse): Promise<ApiResult<void, AuthFetchError | Error>> => {
  if (!uid) {
    return new ApiResult<void, AuthFetchError | Error>(null, true, new Error('No user id provided'))
  }

  try {
    const response = await authFetch<void>({
      path: `hager-energy-api/users/${uid}/client`,
      method: 'DELETE',
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void, AuthFetchError | Error>(null, false)
    }

    return new ApiResult<void, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}
